// import { Grid } from '@mui/material'
import DragEventService, { EventPosition } from 'drag-event-service'

import { memo, useCallback, useEffect, useRef } from 'react'
import { setChatMinimized } from '../../../../../../actions/layout'
import { sendPrivateMessage } from '../../../../../../actions/sendPrivateMessage'
import { sendRoomMessage } from '../../../../../../actions/sendRoomMessage'
import { getScreenState } from '../../../../../../app-state/state.screen'
import { VideoType } from '../../../../../../constants/enums'
import { FloaterSize } from '../../../../../../constants/floaters'
import { avatarUrl } from '../../../../../../mfcLogic/avatarPreviewUrl'
import { RoomView } from '../../../../../../state/modals'
import {
    closeDrawer,
    openWhiteboardDrawer,
} from '../../../../../../state/modals/state.drawer'
import { toggleFloaterVideo } from '../../../../../../state/modals/state.floater'
import {
    LAYOUTSTATE,
    updatePortalState,
} from '../../../../../../state/state.layout'
import { enableRoundedVideo } from '../../../../../../state/state.video'
import {
    closeWhiteboard,
    openWhiteboard,
} from '../../../../../../state/state.whiteboard'
import { calcWidth } from '../../../../../../utilities/dimensions'
import i18n from '../../../../../../utilities/i18n'
import ButtonShare from '../../../../../Buttons/ButtonShare'
import ButtonVideoDrag from '../../../../../Buttons/ButtonVideoDrag'
import { useLayoutSelector } from '../../../../../Hooks/useLayout'
import { usePortalStateSelector } from '../../../../../Hooks/usePortalState'
import { useRoomSelector } from '../../../../../Hooks/useRoomAppState'
import { useUserSelector } from '../../../../../Hooks/useUser'
import { useVideoPlaying } from '../../../../../Hooks/useVideo'
import { useWhiteboardSelector } from '../../../../../Hooks/useWhiteboard'
import FlexGrid from '../../../../../common/FlexGrid'
import { COMPONENT_HEIGHTS, COMPONENT_WIDTHS } from '../../../../../theme'
import { Profile } from '../../../../Profile'
import Video from '../../../../Video'
import Whiteboard, { WhiteboardRoot } from '../../../../Whiteboard'
import ChatActionsBar from '../../../ActionsBar'
import AvailableSpace from '../../../AvailableSpace'
import ChatHeader from '../../../ChatHeader'
import Compose from '../../../Compose'
import {
    calcVideoDimensions,
    getDefaultDivision,
    getDivision,
} from '../../../common/utilities'
import Chat from '../../Chat'
import VideoPoster from '../../VideoPoster'
import { ViewProps, getLeftStyles, getRightStyles } from '../common/helpers'
import {
    AvatarImage,
    chatGridStyles,
    chatPaneStyles,
    gridRootCenter,
    gridRootStyles,
    offlineButtonsStyles,
    opaqueStyle,
    rootStyles,
    spacerStyles,
    streamContainerStyles,
    videoGridStyles,
} from '../common/styles'

const Landscape = (props: ViewProps) => {
    const {
        id,
        size,
        roomView,
        height,
        width,
        disableKeyboardOpenDetection,
        viewingTips,
        showLoungePreviews,
        isSurrogateBroadcasting,

        toggleTips,
        setLoungePreviews,
    } = props

    const chatGridContainerRef = useRef<HTMLDivElement>(null)
    const dragThrottle = useRef<number>(0)
    const draggable = useRef<HTMLButtonElement>(null)
    const savedDivision = useRef<number>(undefined)

    const isLounge = useRoomSelector(id, 'isLounge')
    const loungeSurrogateID = useRoomSelector(id, 'loungeSurrogateID')
    const isWhiteboardOpen = useWhiteboardSelector('open')
    const minimized = useLayoutSelector('minimized')
    const composeFocused = usePortalStateSelector('composeFocused')

    const surrogateName = useUserSelector(loungeSurrogateID, 'name')
    const hasWhiteboard = useUserSelector(id, 'hasWhiteboard')
    const isModel = useUserSelector(id, 'isModel') || isLounge
    const isOffline = useUserSelector(id, 'isOffline')
    const isBroadcasting = useUserSelector(id, 'isBroadcasting')
    const isPublicShow = useUserSelector(id, 'isPublicShow')
    const clubShowOp = useUserSelector(id, 'clubShow', {}, true, 'op')
    const isAway = useUserSelector(id, 'isAway')
    const name = useUserSelector(id, 'name')
    const shareAlbums = useUserSelector(id, 'shareAlbums')
    const shareFollows = useUserSelector(id, 'shareFollows')
    const shareRecordings = useUserSelector(id, 'shareRecordings')
    const shareThings = useUserSelector(id, 'shareThings')
    const shareTipmenus = useUserSelector(id, 'shareTipmenus')
    const slug = useUserSelector(id, 'slug')
    const videoPlaying = useVideoPlaying(id)

    const isPM = roomView === RoomView.PM
    const isProfile = roomView === RoomView.Profile

    const modelName = !isLounge ? name : surrogateName
    const modelID = !isLounge ? id : loungeSurrogateID

    const showVideo = isPM || isProfile ? props.showVideo : true
    const noVideoDefault = !isBroadcasting || isAway
    const noProfileVideo =
        isProfile && (noVideoDefault || !showVideo || isLounge)

    const isModelBroadcasting = isLounge
        ? isSurrogateBroadcasting
        : isBroadcasting

    const hasShare =
        shareAlbums + shareRecordings + shareTipmenus + shareThings > 0 ||
        shareFollows > 0

    const portalDivision =
        usePortalStateSelector('division') || getDefaultDivision(width, height)
    const division = isProfile ? 50 : portalDivision

    const maxVideoWidth = (width * division) / 100
    const { height: calcVideoHeight = 0, width: videoWidth = 0 } =
        calcVideoDimensions(maxVideoWidth, height, false, false)
    const profileImageHeight = maxVideoWidth //(height * 9) / 16 //calcZoomedVideoHeight(videoHeight, maxVideoWidth)
    const videoHeight = noProfileVideo ? profileImageHeight : calcVideoHeight
    const availableHeightBelowVideo = videoHeight
        ? Math.max(
              height -
                  videoHeight -
                  (COMPONENT_HEIGHTS.actionsBar + COMPONENT_HEIGHTS.chatHeader),
              0,
          )
        : 0
    const videoDragTop = !videoHeight
        ? undefined
        : COMPONENT_HEIGHTS.actionsBar + videoHeight / 2 - 16

    const leftStyles = getLeftStyles({
        isLounge,
        isModel,
        division,
    })

    const rightStyles = getRightStyles({
        isModel,
        division,
    })

    const onCloseWhiteboard = useCallback(() => {
        closeWhiteboard()
        if (chatGridContainerRef.current) {
            chatGridContainerRef.current.scrollTop = 0
        }
    }, [])
    const scrollWhiteboard = useCallback(() => {
        if (isProfile) {
            chatGridContainerRef.current.scroll({
                top: 0,
                behavior: 'smooth',
            })
        } else
            chatGridContainerRef.current.scroll({
                top: height - COMPONENT_HEIGHTS.chatHeader,
                behavior: 'smooth',
            })
    }, [height, videoHeight, isProfile])
    const onToggleWhiteboard = useCallback(() => {
        if (isProfile) {
            if (isWhiteboardOpen) {
                closeDrawer(true)
                closeWhiteboard()
            } else {
                openWhiteboardDrawer(modelName)
            }
            return
        }
        if (isWhiteboardOpen) {
            onCloseWhiteboard()
        } else {
            openWhiteboard(modelName)
        }
    }, [isWhiteboardOpen, isProfile, modelName])

    const onToggleStream = useCallback(() => {
        toggleFloaterVideo()
    }, [])

    const onDrag = useCallback(
        (event: MouseEvent | TouchEvent, position: EventPosition) => {
            event.stopPropagation()
            event.preventDefault()
            const { roomHeight, roomWidth } = getScreenState()
            const { pageX: x } = position
            const offsetX = LAYOUTSTATE.layout.expandedNav
                ? COMPONENT_WIDTHS.leftNavExpanded
                : COMPONENT_WIDTHS.leftNav
            const nextVideoWidth = Math.min(
                x - offsetX,
                calcWidth(
                    roomHeight -
                        (COMPONENT_HEIGHTS.actionsBar +
                            COMPONENT_HEIGHTS.chatHeader),
                ),
            )
            const nextDivision = getDivision(nextVideoWidth, roomWidth)
            if (!nextDivision || nextDivision === savedDivision.current) return
            savedDivision.current = nextDivision
            cancelAnimationFrame(dragThrottle.current)
            dragThrottle.current = requestAnimationFrame(() => {
                updatePortalState({ division: savedDivision.current })
            })
        },
        [],
    )

    const onDragEnd = useCallback((event: MouseEvent | TouchEvent) => {
        event.stopPropagation()
        event.preventDefault()
        DragEventService.off(document, 'move', onDrag)
        DragEventService.off(document, 'end', onDragEnd)
    }, [])

    const onDragStart = useCallback((event: MouseEvent | TouchEvent) => {
        event.stopPropagation()
        event.preventDefault()
        DragEventService.on(document, 'move', onDrag, {
            touchArgs: [{ passive: false }],
        })
        DragEventService.on(document, 'end', onDragEnd, {
            touchArgs: [{ passive: false }],
        })
    }, [])

    const draggableRef = useCallback((ref: HTMLButtonElement) => {
        if (ref) {
            draggable.current = ref

            DragEventService.on(ref, 'start', onDragStart, {
                touchArgs: [{ passive: false }],
            })
        } else {
            if (draggable.current) {
                DragEventService.off(draggable.current, 'start', onDragStart)
            }
        }
    }, [])

    useEffect(() => {
        if (!composeFocused && chatGridContainerRef.current) {
            chatGridContainerRef.current.scrollTop = 0
        }
    }, [composeFocused])

    useEffect(() => {
        return () => {
            cancelAnimationFrame(dragThrottle.current)
            DragEventService.off(document, 'move', onDrag)
            DragEventService.off(document, 'end', onDragEnd)
        }
    }, [])
    // handle video rounding css
    useEffect(() => {
        enableRoundedVideo(
            modelID,
            false,
            !isModelBroadcasting ? false : !noProfileVideo,
            size,
            false,
        )
    }, [
        modelID,
        size,
        noProfileVideo,
        videoPlaying,
        isModelBroadcasting,
        clubShowOp,
        isAway,
        isPublicShow,
    ])

    const Header = (
        <ChatHeader
            id={id}
            preview={size < FloaterSize.Fullscreen}
            showBadge={isProfile || (isPM && !showVideo)}
            isProfile={isProfile}
            pm={isPM}
            room
        />
    )
    const ActionsBar = (
        <ChatActionsBar
            id={id}
            floating
            roomView={roomView}
            video={!noVideoDefault ? showVideo : undefined}
            onWhiteboard={onToggleWhiteboard}
            onVideoToggle={!noVideoDefault ? onToggleStream : undefined}
        />
    )
    const ChatInputBar = (
        <Compose
            hide={minimized}
            id={id}
            onSend={isPM ? sendPrivateMessage : sendRoomMessage}
            setMinimized={setChatMinimized}
            placeholder={i18n.t(
                isPM ? 'chat.sendPrivateMessage' : 'chat.sendChatMessage',
            )}
            hideTipButton={!isModel}
            disableKeyboardOpenDetection={disableKeyboardOpenDetection}
            pm={isPM}
        />
    )

    // console.log('[FloatingPortal.Landscape]', {
    //     roomView,
    //     noProfileVideo,
    //     isModelBroadcasting,
    //     isAway,
    //     isPublicShow,
    //     noVideoDefault,
    //     showVideo,
    // })
    const avatarSrc = avatarUrl(id, 300)

    return (
        <div css={rootStyles}>
            {/* [landscape] ChatHeader is above video */}
            {isModel && !(isLounge && isProfile) && Header}
            <FlexGrid
                overrides={[
                    gridRootStyles,
                    !isModel && isProfile && gridRootCenter,
                ]}
                direction="row"
                wrap="wrap"
            >
                {/* [Video Section] we're in a model's room or one of the lounges */}
                {isModel && (
                    <FlexGrid
                        overrides={videoGridStyles}
                        style={{
                            maxWidth: leftStyles === 'auto' ? null : leftStyles,
                            flexBasis: leftStyles,
                        }}
                        data-testid="Video__Section"
                    >
                        {/* [landscape] chat actions bar is above video */}
                        {!(isLounge && isProfile) && ActionsBar}
                        {/* in a model's room and allowed to view her stream or in the lounge */}
                        <div
                            data-testid="Container__StreamContainer"
                            css={streamContainerStyles}
                            style={
                                availableHeightBelowVideo > 0 && videoHeight > 0
                                    ? { height: videoHeight }
                                    : null
                            }
                        >
                            {/* show mfc share button if the model is offline and has share content */}
                            {!isLounge && hasShare && isOffline && (
                                <div css={offlineButtonsStyles}>
                                    <ButtonShare slug={slug} />
                                </div>
                            )}
                            {/* hide video if in the lounge with no model selected */}
                            {isLounge &&
                                !loungeSurrogateID &&
                                !isProfile && ( // !surrogateID && (
                                    <VideoPoster />
                                )}
                            {(!isLounge ||
                                !!loungeSurrogateID ||
                                isProfile) && (
                                <>
                                    {!noProfileVideo && (
                                        <Video
                                            id={modelID}
                                            allowControls
                                            extendedPreviewLabel
                                            videoType={VideoType.Room}
                                        />
                                    )}
                                    <AvatarImage
                                        data-testid="Container__AvatarImage"
                                        src={avatarSrc}
                                        crossOrigin="anonymous"
                                        height={profileImageHeight}
                                        css={
                                            noProfileVideo ? opaqueStyle : null
                                        }
                                    />
                                </>
                            )}
                        </div>
                        {!(isLounge && isProfile) &&
                            availableHeightBelowVideo > 0 &&
                            videoHeight > 0 && (
                                <div css={spacerStyles}>
                                    {availableHeightBelowVideo >=
                                        COMPONENT_HEIGHTS.availableSpaceMinimum && (
                                        <AvailableSpace
                                            id={modelID}
                                            loading={false}
                                            isLounge={isLounge}
                                            height={availableHeightBelowVideo}
                                            width={videoWidth}
                                            roomView={roomView}
                                            onToggleWhiteboard={
                                                onToggleWhiteboard
                                            }
                                        />
                                    )}
                                </div>
                            )}
                    </FlexGrid>
                )}
                {/* [Chat Section] if we're allowed in */}
                <FlexGrid
                    ref={chatGridContainerRef}
                    overrides={chatGridStyles}
                    style={{
                        flexBasis: rightStyles,
                        maxWidth: rightStyles,
                        overflowX: 'hidden',
                        overflowY: isWhiteboardOpen ? 'scroll' : 'hidden',
                    }}
                    data-testid="Chat__Section"
                >
                    {!isProfile && (
                        <ButtonVideoDrag
                            ref={draggableRef}
                            hide={!isModel}
                            top={videoDragTop}
                        />
                    )}
                    <div css={chatPaneStyles} data-testid="Chat__Container">
                        {isProfile ? (
                            <Profile slugOrId={id} />
                        ) : (
                            <>
                                <Chat
                                    key={`chat-${id}`}
                                    id={id}
                                    height={height}
                                    showingTips={viewingTips}
                                    onTipToggle={toggleTips}
                                    onOpenWhiteboard={onToggleWhiteboard}
                                    styleHeight={'100%'}
                                    showLoungePreviews={showLoungePreviews}
                                    setLoungePreviews={setLoungePreviews}
                                    pm={isPM}
                                />
                                {!showLoungePreviews && ChatInputBar}
                            </>
                        )}
                    </div>
                    {hasWhiteboard && isWhiteboardOpen && (
                        <WhiteboardRoot>
                            <Whiteboard
                                identifier={slug}
                                showAvatar={false}
                                scrollIntoView={scrollWhiteboard}
                                onClose={onCloseWhiteboard}
                            />
                        </WhiteboardRoot>
                    )}
                </FlexGrid>
            </FlexGrid>
        </div>
    )
}

export default memo(Landscape)
